import { useEffect, useRef, useState } from "react";
import { Upload, Eye, Loader2, FileText, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ConfirmDelete from "@/components/ConfirmDelete";
import { docStatus, DOC_STATUS } from "@/lib/compliance";

function useFileUrl(api, base, rec) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    setUrl(null);
    if (!rec?.file_type?.startsWith("image/") || !rec?.updated_at) return;
    let objectUrl;
    api.get(`${base}/${encodeURIComponent(rec.item_id)}/file`, { responseType: "blob" })
      .then(({ data }) => { objectUrl = URL.createObjectURL(data); setUrl(objectUrl); })
      .catch(() => {});
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [api, base, rec?.item_id, rec?.updated_at, rec?.file_type]);
  return url;
}

export default function ComplianceItem({ item, rec, api, base, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [broken, setBroken] = useState(false);
  const thumb = useFileUrl(api, base, rec);
  useEffect(() => { setBroken(false); }, [thumb]);
  const st = item.dated ? docStatus(rec?.expires) : null;
  const tid = item.id.replace(/[^a-z0-9]+/gi, "-");
  const path = `${base}/${encodeURIComponent(item.id)}`;

  const saveDate = async (v) => {
    try {
      await api.put(path, { expires: v || null, label: item.notifyLabel || item.label });
      onChanged();
    } catch {
      toast.error("Could not save the date.");
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`${path}/file`, fd);
      toast.success("Uploaded");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
      fileRef.current.value = "";
    }
  };

  const remove = async () => {
    try {
      await api.delete(`${path}/file`);
      toast.success("Document deleted", {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`${path}/file/restore`);
              toast.success("Document restored");
              onChanged();
            } catch {
              toast.error("Could not restore — a new file may have been uploaded.");
            }
          },
        },
      });
      onChanged();
    } catch {
      toast.error("Could not delete the document.");
    }
  };

  const view = async () => {
    const w = window.open("", "_blank");
    const { data } = await api.get(`${path}/file`, { responseType: "blob" });
    w.location.href = URL.createObjectURL(data);
  };

  return (
    <div className="p-4 border-b border-[#262626] last:border-b-0 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`compliance-item-${tid}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {item.photo && (
          <div className="w-20 h-14 shrink-0 bg-[#0A0A0A] border border-[#262626] flex items-center justify-center overflow-hidden">
            {thumb && !broken ? <img src={thumb} alt={item.label} onError={() => setBroken(true)} className="w-full h-full object-cover" /> : <FileText size={18} className="text-muted-foreground" />}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-sm font-semibold">{item.label}</div>
          <div className="text-xs text-muted-foreground truncate">{rec?.file_name || "No file uploaded"}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {st && <span className={`font-mono-label text-[10px] px-2 py-1 ${DOC_STATUS[st].cls}`} data-testid={`status-${tid}`}>{DOC_STATUS[st].label}</span>}
        {item.dated && (
          <input type="date" defaultValue={rec?.expires || ""} key={rec?.expires || "none"} onChange={(e) => saveDate(e.target.value)}
            data-testid={`date-${tid}`} aria-label={`${item.label} expiration date`}
            className="min-h-[44px] bg-[#141414] border border-[#262626] px-3 text-base sm:text-sm [color-scheme:dark]" />
        )}
        <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`file-input-${tid}`} />
        <button onClick={() => fileRef.current.click()} disabled={busy} data-testid={`upload-${tid}`}
          className="min-h-[44px] px-3 border border-[#262626] hover:border-white font-mono-label text-[11px] inline-flex items-center gap-1.5 transition-colors disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : rec?.file_name ? <RefreshCw size={14} /> : <Upload size={14} />} {rec?.file_name ? "Replace" : "Upload"}
        </button>
        {rec?.file_name && (
          <button onClick={view} data-testid={`view-${tid}`} className="min-h-[44px] px-3 border border-[#262626] hover:border-white font-mono-label text-[11px] inline-flex items-center gap-1.5 transition-colors">
            <Eye size={14} /> View
          </button>
        )}
        {rec?.file_name && (
          <ConfirmDelete testid={`delete-${tid}`} onConfirm={remove} title="Delete this document?"
            description={`"${rec.file_name}" will be removed from ${item.label}. ${item.dated ? "The expiration date stays so you can upload the renewed copy." : "You can upload a new photo anytime."}`}>
            <button data-testid={`delete-${tid}`} aria-label={`Delete ${item.label} document`}
              className="min-h-[44px] px-3 border border-[#262626] hover:border-[#DC2626] hover:text-[#F87171] font-mono-label text-[11px] inline-flex items-center gap-1.5 transition-colors">
              <Trash2 size={14} /> Delete
            </button>
          </ConfirmDelete>
        )}
      </div>
    </div>
  );
}
